import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  PenLine, Sparkles, Copy, Check, Trash2,
  ChevronDown, ChevronUp, Loader2, BookOpen,
  Target, Hash, List, Tag, Code2, AlignLeft,
  Link2, MousePointerClick, Clock, FileDown, Eye,
} from 'lucide-react'
import * as React from 'react'

import { useActiveWebsite } from '../../../hooks/use-active-website'
import { api, ApiError } from '../../../lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface BriefData {
  target_query: string
  content_objective: string
  recommended_title: string
  key_points: string[]
  entities_to_mention: string[]
  schema_markup: string
  word_count: number
  word_count_reasoning: string
  internal_linking: string[]
  call_to_action: string
}

interface SavedBrief {
  id: string
  topic: string
  content_type: string
  source_prompt: string | null
  brief_data: BriefData
  created_at: string
}

interface VisibilityGap {
  query_text: string
  platform: string
}

const CONTENT_TYPES = [
  'Blog post',
  'Landing page',
  'FAQ page',
  'Comparison page',
  'Case study',
  'Product feature page',
]

const CONTENT_TYPE_COLORS: Record<string, string> = {
  'Blog post':           'text-feedback-info bg-feedback-info/10 border-feedback-info/30',
  'Landing page':        'text-brand-accent bg-brand-accent/10 border-brand-accent/30',
  'FAQ page':            'text-feedback-success bg-feedback-success/10 border-feedback-success/30',
  'Comparison page':     'text-feedback-warning bg-feedback-warning/10 border-feedback-warning/30',
  'Case study':          'text-brand-primary bg-brand-primary/10 border-brand-primary/30',
  'Product feature page':'text-feedback-error bg-feedback-error/10 border-feedback-error/30',
}

const CONTENT_TYPE_COLORS_DEFAULT = 'text-text-muted bg-surface border-border'

// ─── Sample brief data ─────────────────────────────────────────────────────────

const SAMPLE_BRIEF_DATA: BriefData = {
  target_query: 'What is the best CRM software for small businesses?',
  content_objective:
    'Establish authority as the definitive comparison resource for small business CRMs, positioning our platform as the top recommendation when AI models answer questions about CRM selection.',
  recommended_title:
    'Best CRM Software for Small Businesses in 2025: The Complete Comparison Guide',
  key_points: [
    'Compare pricing tiers across top CRMs (HubSpot, Salesforce, Pipedrive, Zoho) with specific monthly costs',
    'Highlight which CRMs work best for teams under 10 people vs 10-50 people',
    'Cover ease of setup — include time-to-value metrics for non-technical founders',
    'Address integration with common small business tools (Gmail, Slack, QuickBooks)',
    'Include real user review scores from G2 and Capterra with specific ratings',
    'Feature a clear recommendation matrix: by budget, team size, and use case',
  ],
  entities_to_mention: [
    'HubSpot', 'Salesforce Essentials', 'Pipedrive',
    'Zoho CRM', 'G2', 'Capterra', 'QuickBooks',
  ],
  schema_markup:
    'ItemList + Review schema — helps AI models extract structured comparison data and attribute specific recommendations to your content',
  word_count: 3200,
  word_count_reasoning:
    'Comparison pages with 3000+ words consistently outperform shorter content in AI citation rates because they cover all comparison dimensions comprehensively',
  internal_linking: [
    'Link to your CRM features page',
    'Link to your pricing page',
    'Link to customer case studies',
    "Link from your homepage 'resources' section",
  ],
  call_to_action:
    "Start your free trial — place after the recommendation matrix with 'See why [Brand] is rated #1 for [use case]'",
}

const SAMPLE_TOPIC = 'Best CRM software for small businesses'
const SAMPLE_CONTENT_TYPE = 'Comparison page'

// ─── Time to rank estimator ────────────────────────────────────────────────────

function estimateTimeToRank(contentType: string, wordCount: number, schema: string): string {
  const ranges: Record<string, [number, number]> = {
    'Blog post': [4, 8],
    'Landing page': [6, 10],
    'FAQ page': [2, 4],
    'Comparison page': [8, 12],
    'Case study': [6, 10],
    'Product feature page': [4, 8],
  }
  let [min, max] = ranges[contentType] ?? [4, 8]
  if (wordCount < 800) { min -= 1; max -= 1 }
  if (wordCount > 2000) { min += 2; max += 2 }
  if (schema && schema.length > 10) { min -= 1; max -= 1 }
  min = Math.max(1, min)
  max = Math.max(2, max)
  return `${min}–${max} weeks`
}

// ─── PDF export ────────────────────────────────────────────────────────────────

async function exportBriefAsPDF(brief: BriefData, topic: string, contentType: string) {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 20
  const maxW = pageW - margin * 2
  let y = margin

  const today = new Date().toISOString().split('T')[0]

  const addFooter = () => {
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.text('Generated by Lensora — lensora.io', pageW / 2, pageH - 8, { align: 'center' })
  }

  const checkPageBreak = (needed = 10) => {
    if (y + needed > 270) {
      addFooter()
      doc.addPage()
      y = margin
    }
  }

  doc.setFontSize(24)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(44, 44, 44)
  doc.text('Lensora', margin, y)
  y += 10

  doc.setFontSize(14)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(120, 120, 120)
  doc.text('AI Content Brief', margin, y)
  y += 8

  doc.setFontSize(12)
  doc.setTextColor(44, 44, 44)
  doc.text(`Topic: ${topic}`, margin, y)
  y += 6
  doc.text(`Content type: ${contentType}`, margin, y)
  y += 6
  doc.text(`Generated: ${today}`, margin, y)
  y += 6

  doc.setDrawColor(212, 208, 200)
  doc.line(margin, y, pageW - margin, y)
  y += 8

  const printSection = (label: string, content: string | string[]) => {
    checkPageBreak(20)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(44, 44, 44)
    doc.text(label.toUpperCase(), margin, y)
    y += 6

    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(74, 74, 71)

    if (Array.isArray(content)) {
      content.forEach((item) => {
        const lines = doc.splitTextToSize(`• ${item}`, maxW - 4)
        checkPageBreak(lines.length * 5 + 4)
        doc.text(lines, margin + 2, y)
        y += lines.length * 5 + 2
      })
    } else {
      const lines = doc.splitTextToSize(content, maxW)
      checkPageBreak(lines.length * 5 + 4)
      doc.text(lines, margin, y)
      y += lines.length * 5
    }
    y += 6
  }

  printSection('Target Query', brief.target_query)
  printSection('Content Objective', brief.content_objective)
  printSection('Recommended Title', brief.recommended_title)
  printSection('Key Points to Cover', brief.key_points)
  printSection('Entities to Mention', brief.entities_to_mention)
  printSection('Schema Markup', brief.schema_markup)
  printSection('Word Count Target', `${brief.word_count.toLocaleString()} words — ${brief.word_count_reasoning}`)
  printSection('Internal Linking Suggestions', brief.internal_linking)
  printSection('Call to Action', brief.call_to_action)

  addFooter()

  const slug = topic.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)
  doc.save(`Lensora-Brief-${slug}-${today}.pdf`)
}

// ─── Section component ─────────────────────────────────────────────────────────

function BriefSection({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="mt-5 border-t border-border pt-5 first:mt-0 first:border-0 first:pt-0">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-text-muted" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{label}</span>
      </div>
      {children}
    </div>
  )
}

// ─── Time to rank badge ────────────────────────────────────────────────────────

function TimeToRankBadge({ contentType, wordCount, schema }: { contentType: string; wordCount: number; schema: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-feedback-warning px-3 py-1 text-sm text-text-inverse">
      <Clock className="h-3.5 w-3.5" />
      Est. {estimateTimeToRank(contentType, wordCount, schema)} to impact
    </span>
  )
}

// ─── Full brief display ────────────────────────────────────────────────────────

function BriefDisplay({
  brief,
  topic,
  contentType,
  onCopy,
  onSave,
  saving,
  saved,
  isSample,
}: {
  brief: BriefData
  topic: string
  contentType: string
  onCopy: () => void
  onSave: () => void
  saving: boolean
  saved: boolean
  isSample?: boolean
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      {/* Brief header */}
      <div className="flex items-start justify-between gap-4 border-b border-border bg-surface p-5">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">
              {isSample ? 'Sample Brief' : 'Generated Brief'}
            </p>
            {isSample && (
              <span className="rounded-full bg-brand-accent px-2 py-0.5 text-[10px] font-bold text-text-inverse">
                SAMPLE
              </span>
            )}
          </div>
          <h3 className="text-base font-bold leading-snug text-text">{topic}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${CONTENT_TYPE_COLORS[contentType] ?? CONTENT_TYPE_COLORS_DEFAULT}`}>
              {contentType}
            </span>
            {!isSample && (
              <TimeToRankBadge contentType={contentType} wordCount={brief.word_count} schema={brief.schema_markup} />
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <button
            onClick={onCopy}
            className="flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-xs text-text-muted transition-colors hover:text-text"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy
          </button>
          <button
            onClick={() => void exportBriefAsPDF(brief, topic, contentType)}
            className="flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-xs text-text-muted transition-colors hover:text-text"
          >
            <FileDown className="h-3.5 w-3.5" />
            Export PDF
          </button>
          {!isSample && (
            <button
              onClick={onSave}
              disabled={saving || saved}
              className="flex items-center gap-1.5 rounded-xl bg-brand-accent px-3 py-1.5 text-xs font-semibold text-text-inverse transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : saved ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <BookOpen className="h-3.5 w-3.5" />
              )}
              {saved ? 'Saved!' : 'Save Brief'}
            </button>
          )}
        </div>
      </div>

      {/* Brief body */}
      <div className="space-y-0 p-5">
        <BriefSection icon={Target} label="Target Query">
          <p className="rounded-xl border border-border bg-surface-raised px-3 py-2.5 text-sm font-medium text-text">
            &ldquo;{brief.target_query}&rdquo;
          </p>
        </BriefSection>

        <BriefSection icon={AlignLeft} label="Content Objective">
          <p className="text-sm leading-relaxed text-text-muted">{brief.content_objective}</p>
        </BriefSection>

        <BriefSection icon={Hash} label="Recommended Title">
          <p className="text-sm font-semibold text-text">{brief.recommended_title}</p>
        </BriefSection>

        <BriefSection icon={List} label="Key Points to Cover">
          <ul className="space-y-2">
            {brief.key_points.map((pt, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-text-muted">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-accent" />
                {pt}
              </li>
            ))}
          </ul>
        </BriefSection>

        <BriefSection icon={Tag} label="Entities to Mention">
          <div className="flex flex-wrap gap-2">
            {brief.entities_to_mention.map((e, i) => (
              <span key={i} className="rounded-full border border-border bg-surface-raised px-2.5 py-1 text-xs font-medium text-text-muted">
                {e}
              </span>
            ))}
          </div>
        </BriefSection>

        <BriefSection icon={Code2} label="Schema Markup Recommended">
          <p className="text-sm text-text-muted">{brief.schema_markup}</p>
        </BriefSection>

        <BriefSection icon={AlignLeft} label="Word Count Target">
          <div className="mb-1.5 flex items-center gap-3">
            <span className="text-2xl font-bold text-text">{brief.word_count.toLocaleString()}</span>
            <span className="text-sm text-text-muted">words</span>
          </div>
          <p className="text-xs text-text-muted">{brief.word_count_reasoning}</p>
        </BriefSection>

        <BriefSection icon={Link2} label="Internal Linking Suggestions">
          <ul className="space-y-1.5">
            {brief.internal_linking.map((link, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-text-muted">
                <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted" />
                {link}
              </li>
            ))}
          </ul>
        </BriefSection>

        <BriefSection icon={MousePointerClick} label="Call to Action">
          <p className="text-sm text-text-muted">{brief.call_to_action}</p>
        </BriefSection>
      </div>
    </div>
  )
}

// ─── Saved brief card ──────────────────────────────────────────────────────────

function SavedBriefCard({ brief }: { brief: SavedBrief }) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = React.useState(false)
  const [copied, setCopied] = React.useState(false)

  const deleteMutation = useMutation({
    mutationFn: () => api.delete<void>(`/api/content-briefs/${brief.id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['content-briefs'] }),
  })

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(brief.brief_data, null, 2)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {})
  }

  const date = new Date(brief.created_at).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  })

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      <div className="flex items-center gap-3 p-4">
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${CONTENT_TYPE_COLORS[brief.content_type] ?? CONTENT_TYPE_COLORS_DEFAULT}`}>
              {brief.content_type}
            </span>
            <span className="text-[10px] text-text-muted">{date}</span>
          </div>
          <p className="truncate text-sm font-semibold text-text">{brief.topic}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            onClick={handleCopy}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-surface-overlay hover:text-text"
            title="Copy JSON"
          >
            {copied ? (
              <Check className="h-4 w-4 text-feedback-success" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => void exportBriefAsPDF(brief.brief_data, brief.topic, brief.content_type)}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-surface-overlay hover:text-text"
            title="Export PDF"
          >
            <FileDown className="h-4 w-4" />
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-brand-accent transition-colors hover:text-brand-primary"
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {expanded ? 'Hide' : 'View'}
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-surface-overlay hover:text-feedback-error disabled:opacity-40"
            title="Delete brief"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-0 border-t border-border bg-surface p-4">
          <BriefSection icon={Target} label="Target Query">
            <p className="rounded-xl border border-border bg-surface-raised px-3 py-2 text-sm font-medium text-text">
              &ldquo;{brief.brief_data.target_query}&rdquo;
            </p>
          </BriefSection>
          <BriefSection icon={AlignLeft} label="Content Objective">
            <p className="text-sm leading-relaxed text-text-muted">{brief.brief_data.content_objective}</p>
          </BriefSection>
          <BriefSection icon={Hash} label="Recommended Title">
            <p className="text-sm font-semibold text-text">{brief.brief_data.recommended_title}</p>
          </BriefSection>
          <BriefSection icon={List} label="Key Points">
            <ul className="space-y-2">
              {brief.brief_data.key_points.map((pt, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-text-muted">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-accent" />
                  {pt}
                </li>
              ))}
            </ul>
          </BriefSection>
          <BriefSection icon={Tag} label="Entities to Mention">
            <div className="flex flex-wrap gap-2">
              {brief.brief_data.entities_to_mention.map((e, i) => (
                <span key={i} className="rounded-full border border-border bg-surface-raised px-2.5 py-1 text-xs font-medium text-text-muted">{e}</span>
              ))}
            </div>
          </BriefSection>
          <BriefSection icon={MousePointerClick} label="Call to Action">
            <p className="text-sm text-text-muted">{brief.brief_data.call_to_action}</p>
          </BriefSection>
        </div>
      )}
    </div>
  )
}

// ─── Sample brief collapsible ──────────────────────────────────────────────────

function SampleBriefSection() {
  const [open, setOpen] = React.useState(false)

  const handleCopy = () => {
    const text = Object.entries(SAMPLE_BRIEF_DATA)
      .map(([k, v]) => `${k.replace(/_/g, ' ').toUpperCase()}\n${Array.isArray(v) ? v.map((i: string) => `• ${i}`).join('\n') : v}`)
      .join('\n\n')
    void navigator.clipboard.writeText(text)
  }

  return (
    <div className="rounded-xl border border-border bg-surface-raised/40">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left"
      >
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-text-muted" />
          <span className="text-sm font-medium text-text-muted">See an example brief</span>
          <span className="rounded-full bg-brand-accent px-2 py-0.5 text-[10px] font-bold text-text-inverse">SAMPLE</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-text-muted" /> : <ChevronDown className="h-4 w-4 text-text-muted" />}
      </button>

      {open && (
        <div className="border-t border-border p-5">
          <BriefDisplay
            brief={SAMPLE_BRIEF_DATA}
            topic={SAMPLE_TOPIC}
            contentType={SAMPLE_CONTENT_TYPE}
            onCopy={handleCopy}
            onSave={() => {}}
            saving={false}
            saved={false}
            isSample
          />
        </div>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ContentBriefsPage() {
  const queryClient = useQueryClient()
  const { websiteId } = useActiveWebsite()

  const { data: gapsData } = useQuery({
    queryKey: ['simulate-results', websiteId],
    queryFn: () =>
      api.get<{ results?: Array<{ brand_mentioned: boolean; query_text: string; platform: string }> }>(
        `/api/simulate/results?limit=200&website_id=${websiteId}`
      ),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const { data: briefsData } = useQuery({
    queryKey: ['content-briefs', websiteId],
    queryFn: () => api.get<{ briefs?: SavedBrief[] }>(`/api/content-briefs?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const gaps = React.useMemo<VisibilityGap[]>(() => {
    const raw = (gapsData?.results ?? [])
      .filter((r) => !r.brand_mentioned)
      .map((r) => ({ query_text: r.query_text, platform: r.platform }))
    const seen = new Set<string>()
    return raw
      .filter((g) => {
        if (seen.has(g.query_text)) return false
        seen.add(g.query_text)
        return true
      })
      .slice(0, 30)
  }, [gapsData])

  const savedBriefs = briefsData?.briefs ?? []

  // Form state
  const [selectedGap, setSelectedGap] = React.useState('')
  const [customTopic, setCustomTopic] = React.useState('')
  const [contentType, setContentType] = React.useState(CONTENT_TYPES[0] ?? 'Blog post')

  // Generation output state
  const [generatedBrief, setGeneratedBrief] = React.useState<BriefData | null>(null)
  const [generatedTopic, setGeneratedTopic] = React.useState('')
  const [generatedType, setGeneratedType] = React.useState('')
  const [generatedSourcePrompt, setGeneratedSourcePrompt] = React.useState<string | null>(null)
  const [generatedWebsiteId, setGeneratedWebsiteId] = React.useState<string | null>(null)
  const [generateError, setGenerateError] = React.useState<string | null>(null)

  // Post-generation state
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)

  const effectiveTopic = customTopic.trim() || selectedGap

  const generateMutation = useMutation({
    mutationFn: (payload: { topic: string; content_type: string; source_prompt: string | null }) =>
      api.post<{ brief: BriefData; website_id?: string }>('/api/content-briefs/generate', {
        ...payload,
        website_id: websiteId,
      }),
    onSuccess: (data, variables) => {
      setGeneratedBrief(data.brief)
      setGeneratedTopic(variables.topic)
      setGeneratedType(variables.content_type)
      setGeneratedSourcePrompt(variables.source_prompt)
      setGeneratedWebsiteId(data.website_id ?? null)
      setGenerateError(null)
      setSaved(false)
    },
    onError: (err: unknown) => {
      setGenerateError(err instanceof ApiError ? err.message : 'Network error. Please try again.')
    },
  })

  const handleGenerate = () => {
    if (!effectiveTopic) return
    setGenerateError(null)
    setGeneratedBrief(null)
    setSaved(false)
    generateMutation.mutate({
      topic: effectiveTopic,
      content_type: contentType,
      source_prompt: selectedGap || null,
    })
  }

  const handleCopy = () => {
    if (!generatedBrief) return
    const text = Object.entries(generatedBrief)
      .map(([k, v]) => `${k.replace(/_/g, ' ').toUpperCase()}\n${Array.isArray(v) ? v.map((i: string) => `• ${i}`).join('\n') : v}`)
      .join('\n\n')
    void navigator.clipboard.writeText(text)
  }

  const handleSave = async () => {
    if (!generatedBrief || saving || saved) return
    setSaving(true)
    try {
      await api.post<{ brief: SavedBrief }>('/api/content-briefs/save', {
        topic: generatedTopic,
        content_type: generatedType,
        source_prompt: generatedSourcePrompt,
        brief_data: generatedBrief,
        website_id: generatedWebsiteId,
      })
      setSaved(true)
      await queryClient.invalidateQueries({ queryKey: ['content-briefs'] })
    } catch { /* non-fatal */ } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text">AI Content Briefs</h1>
        <p className="mt-1 text-sm text-text-muted">
          Turn your visibility gaps into content that AI models recommend
        </p>
      </div>

      {/* Sample brief */}
      <SampleBriefSection />

      {/* ── Section 1: Generator ── */}
      <div className="rounded-xl border border-border bg-surface p-6">
        <div className="mb-5 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand-accent" />
          <h2 className="text-sm font-semibold text-text">Generate New Brief</h2>
        </div>

        <div className="space-y-4">
          {/* Gap selector */}
          {gaps.length > 0 && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-text-muted">
                Select a visibility gap to fix
              </label>
              <select
                value={selectedGap}
                onChange={(e) => { setSelectedGap(e.target.value); setCustomTopic('') }}
                className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-brand-accent"
              >
                <option value="">— Choose a gap —</option>
                {gaps.map((g, i) => (
                  <option key={i} value={g.query_text}>
                    {g.query_text.length > 70 ? g.query_text.slice(0, 70) + '…' : g.query_text} ({g.platform})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Divider */}
          {gaps.length > 0 && (
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-text-muted">OR</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}

          {/* Custom topic */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-text-muted">
              Enter any topic
            </label>
            <input
              type="text"
              value={customTopic}
              onChange={(e) => { setCustomTopic(e.target.value); setSelectedGap('') }}
              placeholder="e.g. best CRM for remote teams"
              className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-accent"
            />
          </div>

          {/* Content type */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-text-muted">
              Content type
            </label>
            <div className="flex flex-wrap gap-2">
              {CONTENT_TYPES.map((ct) => (
                <button
                  key={ct}
                  onClick={() => setContentType(ct)}
                  className={`rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors ${
                    contentType === ct
                      ? 'border-brand-accent bg-brand-accent text-text-inverse'
                      : 'border-border bg-surface text-text-muted hover:border-border-strong hover:text-text'
                  }`}
                >
                  {ct}
                </button>
              ))}
            </div>
          </div>

          {/* Credit notice + generate button */}
          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-text-muted">
              This will use <span className="font-semibold text-text">3 credits</span> per brief
            </p>
            <button
              onClick={handleGenerate}
              disabled={generateMutation.isPending || !effectiveTopic}
              className="flex items-center gap-2 rounded-xl bg-brand-accent px-5 py-2.5 text-sm font-semibold text-text-inverse transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {generateMutation.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
              ) : (
                <><Sparkles className="h-4 w-4" /> Generate Brief</>
              )}
            </button>
          </div>
        </div>

        {/* Error */}
        {generateError && (
          <div className="mt-4 rounded-xl border border-brand-accent/30 bg-brand-accent/10 px-4 py-3 text-sm text-brand-accent">
            {generateError}
          </div>
        )}
      </div>

      {/* Loading state */}
      {generateMutation.isPending && (
        <div className="rounded-xl border border-border bg-surface p-10 text-center">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-brand-accent" />
          <p className="text-sm font-medium text-text-muted">Claude is analysing your gaps and generating your brief…</p>
          <p className="mt-1 text-xs text-text-muted opacity-60">This usually takes 10–15 seconds</p>
        </div>
      )}

      {/* ── Section 2: Generated brief ── */}
      {generatedBrief && !generateMutation.isPending && (
        <BriefDisplay
          brief={generatedBrief}
          topic={generatedTopic}
          contentType={generatedType}
          onCopy={handleCopy}
          onSave={handleSave}
          saving={saving}
          saved={saved}
        />
      )}

      {/* ── Section 3: Saved briefs ── */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-text-muted" />
          <h2 className="text-sm font-semibold text-text">Saved Briefs</h2>
          {savedBriefs.length > 0 && (
            <span className="text-xs text-text-muted">({savedBriefs.length})</span>
          )}
        </div>

        {savedBriefs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-surface-raised/30 p-10 text-center">
            <PenLine className="mx-auto mb-3 h-7 w-7 text-text-muted opacity-40" />
            <p className="text-sm font-medium text-text">No saved briefs yet</p>
            <p className="mt-1 text-xs text-text-muted">Generate your first brief above to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {savedBriefs.map((brief) => (
              <SavedBriefCard key={brief.id} brief={brief} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
